import type { MergeServices } from 'v5/platform/ts/services';
import type { WithToastService } from 'v5/services/front/ToastService/ToastService';
import type { WithUserPreferencesService } from 'v5/services/front/UserPreferencesService/UserPreferencesService';
import type { WithResourceMutationService } from 'v5/services/io/ResourceMutationService/ResourceMutationService';
import type { WithUploadService } from 'v5/services/io/UploadService/UploadService';
import type { WithAccessService } from 'v5/services/front/AccessService/AccessService';
import type { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import type { WithSystemSettingsService } from 'v5/services/internal/SystemSettingsService/SystemSettingsService';
import { useResourceAssociationActions } from '../useResourceAssociations';
import { useResourcePins } from '../useResourcePins';
import { Pinboard } from './Pinboard';

export type ResourcePinboardServices = MergeServices<
  [
    WithUploadService,
    WithToastService,
    WithResourceMutationService,
    WithUserPreferencesService,
    WithAppScopeService,
    WithSystemSettingsService,
    WithAccessService
  ]
>;

export interface ResourcePinboardProps {
  services: ResourcePinboardServices;
  resourceId: number;
  readOnly: boolean;
  initialPinId?: number;
}

/** The resource's pinboard: its pins and their writes through the v1 store, the profile picture through the resource. */
export function ResourcePinboard({
  services,
  resourceId,
  readOnly,
  initialPinId
}: ResourcePinboardProps) {
  const actions = useResourceAssociationActions(resourceId);
  return (
    <Pinboard
      services={services}
      pins={useResourcePins(resourceId)}
      readOnly={readOnly}
      initialPinId={initialPinId}
      onCreatePin={actions.createPin}
      onDeletePin={actions.deletePin}
      onUpdatePinTags={actions.updatePinTags}
      onReorderPins={actions.reorderPins}
      onSetProfilePicture={url =>
        services.resourceMutationService.updateResource({
          id: resourceId,
          data: { profile_picture: url }
        })
      }
    />
  );
}
